import { TCollection, TNote } from '@ws/shared/types';
import { TGetNodesByRefs } from '@ws/shared/utils';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { computeTkMarkers } from './computeTkMarkers';
import { TTkMarkersState } from './types';

interface IUseTkMarkersParams {
  entity: TCollection | TNote;
  getNodesByRefs: TGetNodesByRefs;
  /**
   * Что-либо, меняющееся вместе с содержимым проекта, — на практике сам объект
   * проекта: он меняется по ссылке на каждое событие.
   */
  revision: unknown;
}

const LOADING: TTkMarkersState = { status: 'loading' };

/** Результат вместе с ключом, для которого он посчитан. */
interface IKeyedResult {
  entityId: string;
  revision: unknown;
  state: TTkMarkersState;
}

/**
 * Список меток, посчитанный ВНЕ рендера.
 *
 * Обход делает глубокую копию каждой заметки и разбирает её HTML — на книге в
 * 300 заметок это сотни миллисекунд. Посчитай мы это прямо в рендере, лоадер бы
 * не появился: заблокирован оказался бы тот самый кадр, в котором он должен был
 * отрисоваться.
 *
 * Пока идёт счёт, наружу отдаётся `loading`, а НЕ прошлый или пустой список:
 * иначе панель на мгновение сказала бы «меток нет» там, где они есть.
 *
 * `getNodesByRefs` в зависимости не входит намеренно — это обычная функция,
 * пересоздаваемая на каждый рендер провайдера, и она гоняла бы пересчёт по кругу.
 * Актуальность берётся через ref; за изменения содержимого отвечает `revision`.
 */
export function useTkMarkers({
  entity,
  getNodesByRefs,
  revision,
}: IUseTkMarkersParams): TTkMarkersState {
  const [computed, setComputed] = useState<IKeyedResult | null>(null);

  const getNodesByRefsRef = useRef(getNodesByRefs);
  useLayoutEffect(() => {
    getNodesByRefsRef.current = getNodesByRefs;
  });

  const entityId = entity.id;
  const entityRef = useRef(entity);
  useLayoutEffect(() => {
    entityRef.current = entity;
  });

  useEffect(() => {
    let cancelled = false;

    const timer = setTimeout(() => {
      const result = computeTkMarkers(entityRef.current, getNodesByRefsRef.current);

      // Счёт мог устареть, пока ждал своей очереди: сущность сменилась или
      // панель закрылась. Результат устаревшего счёта наружу не выпускаем.
      if (!cancelled) {
        setComputed({ entityId, revision, state: { status: 'ready', ...result } });
      }
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [entityId, revision]);

  // Актуальность определяется СИНХРОННО, по ключу результата, а не сбросом из
  // эффекта. Сброс происходит уже после коммита, и промежуточный кадр успел бы
  // показать список предыдущей сущности — а при нуле меток ещё и соврать
  // «здесь всё дописано» про заметку, которую никто не считал.
  const isCurrent =
    computed !== null && computed.entityId === entityId && computed.revision === revision;

  return isCurrent ? computed.state : LOADING;
}
